import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { workIntro, filters, cases, workOrder } from "../data/content";
import { CasePreview } from "../components/visuals";
import { PrintButton } from "../components/PrintButton";

const CaseCard = ({ slug }) => {
  const c = cases[slug];
  return (
    <Link
      to={`/work/${slug}`}
      className="ic-surface group flex flex-col gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-[var(--border-accent)]"
      data-testid={`case-card-${slug}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {c.tags.map((t) => (
          <span key={t} className="ic-tag">{t}</span>
        ))}
      </div>
      <div>
        <p className="font-mono text-[0.62rem] uppercase tracking-wider text-[var(--text-muted)]">{c.name}</p>
        <h3 className="mt-1.5 font-head text-lg font-semibold leading-snug">{c.headline}</h3>
      </div>
      <p className="text-sm leading-relaxed text-[var(--text-2)]">{c.cardOutcome}</p>
      <div className="mt-auto rounded-lg border border-[var(--border)] bg-[var(--surface-2)] p-3">
        <CasePreview visual={c.visual} />
      </div>
      <span className="ic-link text-sm">Explore case <ArrowRight size={14} /></span>
    </Link>
  );
};

export default function Work() {
  const [active, setActive] = useState("All work");

  const visible = workOrder.filter((slug) => active === "All work" || cases[slug].tags.includes(active));

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <h1 className="font-head text-3xl font-bold tracking-tight sm:text-4xl">More work</h1>
          <p className="mt-3 text-lg leading-relaxed text-[var(--text-2)]">{workIntro}</p>
        </div>
        <PrintButton />
      </div>

      {/* Featured */}
      <Link
        to="/work/rsc"
        className="ic-surface mt-8 flex items-center gap-3 p-4 text-sm font-medium transition-colors hover:border-[var(--border-accent)]"
        data-testid="work-featured-rsc"
      >
        <Star size={16} className="text-[var(--accent)]" />
        Featured in the interview: Recruiter System Connect
        <ArrowRight size={14} className="ml-auto text-[var(--text-2)]" />
      </Link>

      {/* Filters */}
      <div className="no-print mt-8 flex flex-wrap gap-2" role="group" aria-label="Filter work by theme">
        {filters.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setActive(f)}
            aria-pressed={active === f}
            data-testid={`work-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
              active === f
                ? "border-transparent bg-[var(--accent)] text-[#05070d]"
                : "border-[var(--border-strong)] text-[var(--text-2)] hover:text-[var(--text)]"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {visible.map((slug) => (
          <CaseCard key={slug} slug={slug} />
        ))}
      </div>
      {visible.length === 0 && (
        <p className="mt-8 text-[var(--text-2)]">No examples under this theme.</p>
      )}
    </div>
  );
}
